/**
 * Tutorial / Help center for the HALO admin panel.
 *
 * Static content — no API calls. Organised as expandable sections that
 * mirror the actual admin tabs so the user can read it like a quick guide
 * the first time they log in.
 */
import { useState } from 'react';
import {
  HelpCircle, ChevronDown, LayoutDashboard, Calendar, Ticket, IdCard,
  FileText, UserPlus, Clock, Tag, MessageSquare, Lightbulb, Phone, Mail, ShieldCheck,
} from 'lucide-react';

const SECTIONS = [
  {
    id: 'intro',
    icon: Lightbulb,
    title: '¿Qué es este panel?',
    bullets: [
      'Es la central de mando de HALO Cuevas de Sal: aquí gestionas reservas, clientes, bonos, facturas y mensajes.',
      'Hay dos roles: admin (acceso total) y office (todo excepto el CMS de contenido).',
      'Los cambios que haces aquí afectan a la web pública en tiempo real (excepto el CMS, que requiere recarga del cliente).',
    ],
  },
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    title: 'Dashboard — pulso del centro',
    bullets: [
      'Muestra: nº de reservas pendientes/confirmadas, ingresos del mes, mensajes nuevos, recordatorios pendientes de enviar.',
      'Pulsa el botón "Enviar recordatorios" para mandar manualmente los emails de mañana (también se envían automáticamente a las 9:00 Madrid).',
      'Acceso rápido a las últimas reservas; haz clic en cualquier reserva para ir a su detalle.',
    ],
  },
  {
    id: 'calendar',
    icon: Calendar,
    title: 'Calendario — vista mensual',
    bullets: [
      'Filtra por Sala 1 (pequeña) o Sala 2 (grande) con los botones superiores.',
      'Click en un día → ves todas las reservas de ese día con su sala (icono 🏛️/🏠) y estado por colores.',
      'Click en una celda vacía para crear una reserva manual (cliente que llamó por teléfono).',
      'Bloquea días con el candado en cada celda, o usa "Bloquear rango" para vacaciones.',
      'Una reserva manual puede asociarse a un código de bono — al guardar, descuenta automáticamente una sesión.',
    ],
  },
  {
    id: 'bonos',
    icon: Ticket,
    title: 'Bonos — packs de sesiones',
    bullets: [
      'Catálogo: tres categorías (Individual, Adulto+Niño, Yoga). Cada bono tiene un código único HALO-XXX-XXXXXX.',
      'Filtros: Pagado / Pendiente / Fallido / Todos.',
      'Cada bono muestra sesiones usadas/totales y permite descontar o restaurar una sesión a mano.',
      'Botón "PDF" descarga el vale regalo del cliente.',
      'Solo el admin puede eliminar un bono (irreversible).',
      'Venta manual: si el cliente paga en local, crea un bono con "Nuevo bono manual" — emite código + envía email.',
    ],
  },
  {
    id: 'customer-cards',
    icon: IdCard,
    title: 'Fichas de clientes — el CRM',
    bullets: [
      'Cada persona tiene un número único HALO-CL-00001. Se reutiliza si vuelve.',
      'Busca por nombre, DNI, teléfono o email — la búsqueda es instantánea.',
      '"Nueva ficha" para crear manualmente; el flujo /reservar la crea automáticamente al recibir un pago.',
      'Botón "Historial" → vista completa del cliente: estadísticas, bonos activos, todas sus reservas con su estado, facturas y condiciones de salud declaradas. Desde ahí descargas los PDFs de consentimientos firmados.',
      'Solo el admin puede eliminar una ficha.',
    ],
  },
  {
    id: 'invoices',
    icon: FileText,
    title: 'Facturas — numeración legal',
    bullets: [
      'Cada factura es HALO-FAC-AAAA-NNNN, secuencial y única por año.',
      'Se generan AUTOMÁTICAMENTE cuando marcas una reserva como "completada" (estado en Reservas).',
      'Al generarse se envía PDF por email al cliente (con copia a info@halocuevas.com).',
      'Exportar CSV → genera un archivo con separador ; para mandar al contable.',
      'Click "PDF" en cada fila para volver a descargar la factura.',
    ],
  },
  {
    id: 'walk-in',
    icon: UserPlus,
    title: 'Alta presencial — cliente que llega al centro',
    bullets: [
      'Úsala cuando alguien aparece sin haber reservado online.',
      'El formulario te lleva por las 6 secciones: tipo, fecha/hora, ficha, salud, consentimientos firmados (papel o tablet), pago.',
      'Al final tienes dos opciones: A) Crear con firma presencial. B) "Enviar enlace para firmar" — manda al cliente un email/WhatsApp para que firme desde su móvil.',
      'Métodos de pago: Efectivo / Tarjeta datafono / Bono (introduce el código).',
      'Al crear se descarga un PDF con la firma y los textos legales.',
    ],
  },
  {
    id: 'signatures',
    icon: ShieldCheck,
    title: 'Firmas — cliente firma desde su móvil',
    bullets: [
      'Cuando eliges "Enviar enlace para firmar" en Alta presencial, el sistema genera un enlace único (válido 7 días) y lo envía por email automáticamente.',
      'También obtienes un mensaje listo para WhatsApp: pulsa "Abrir en WhatsApp" o "Copiar mensaje" para pegarlo en cualquier conversación.',
      'El cliente abre el enlace en su móvil, lee los 3 consentimientos, escribe su nombre (DNI opcional), firma con el dedo y envía. Tú recibes un email cuando lo hace.',
      'En la pestaña "Firmas" ves todas las solicitudes con estado: pendiente / firmado / caducado.',
      'Botón "Reenviar" → si el cliente perdió el email. Botón "PDF firma" → descarga el PDF con la firma incrustada, fecha, hora e IP forense (firma electrónica simple válida bajo RGPD/eIDAS).',
    ],
  },
  {
    id: 'internal-promos',
    icon: Tag,
    title: 'Promos internas — descuentos privados',
    bullets: [
      'Crea descuentos reutilizables que NO aparecen en la web: "Flyer -10€", "Promoción mayo 50%", "Cortesía", etc.',
      'Tres tipos de descuento: Importe fijo (resta euros), Porcentaje (-%), Precio final (fija el total exacto).',
      'Al crear una reserva manual desde el Calendario o en Alta presencial, verás la lista de promos activas y podrás elegirla.',
      'También puedes escribir un precio manual directamente sin promo predefinida — sirve para casos puntuales.',
      'El motivo del descuento (etiqueta) aparece en la factura como línea: "Descuento (Flyer marzo) -10€".',
      'Activa/desactiva una promo sin borrarla con el checkbox "Activa".',
    ],
  },
  {
    id: 'bookings',
    icon: Clock,
    title: 'Reservas — estado y acciones',
    bullets: [
      'Lista de todas las reservas con filtros por estado.',
      'Editar una reserva (cambiar hora, sala, personas, notas).',
      'Cambiar el estado: Pendiente → Confirmada → Completada (genera factura) / Cancelada.',
      '"Reenviar confirmación" manda el email otra vez al cliente.',
      'Solo el admin puede eliminar una reserva (también recupera la sesión del bono asociado si lo pidió).',
    ],
  },
  {
    id: 'promos',
    icon: Tag,
    title: 'Códigos promo',
    bullets: [
      'Crea códigos con descuento % o importe fijo, con fecha de caducidad y usos máximos.',
      'Útil para regalar sesiones (código 100% = regalo, no requiere depósito).',
      'Histórico de usos en cada código.',
    ],
  },
  {
    id: 'contacts',
    icon: MessageSquare,
    title: 'Mensajes — formulario de contacto',
    bullets: [
      'Cada mensaje del formulario "Contacto" llega aquí (con copia a info@halocuevas.com).',
      'Estados: Nuevo / En espera / Contestado — pulsa el botón correspondiente para clasificar.',
      'Botones rápidos: "Responder por email" (abre Gmail) y "Llamar" (abre el marcador).',
    ],
  },
  {
    id: 'workflows',
    icon: Lightbulb,
    title: 'Flujos típicos — paso a paso',
    bullets: [
      '🛒 Cliente compra bono online → pago en Redsys → recibe PDF + email → bono aparece como "Pagado" en la pestaña Bonos.',
      '📞 Cliente llama: ya tiene bono → en Calendario, crea reserva manual y busca su código de bono (se autocompletan sus datos y se descuenta sesión).',
      '👋 Cliente entra sin reservar (firma en local) → "Alta presencial" → rellenas ficha + cuestionario + firma → reserva confirmada al instante.',
      '📱 Cliente entra/llama y prefiere firmar desde su móvil → "Alta presencial" → "Enviar enlace para firmar" → en su móvil firma con el dedo → reserva confirmada automáticamente.',
      '✅ Termina la sesión → vas a Reservas → cambias su estado a "Completada" → la factura se emite y se envía sola.',
      '📅 Vacaciones → Calendario → "Bloquear rango" con motivo → los clientes no podrán reservar esos días en la web.',
    ],
  },
  {
    id: 'troubleshooting',
    icon: HelpCircle,
    title: 'Problemas comunes',
    bullets: [
      'La web sigue sin actualizar tras editar el CMS → pulsa el botón "Deploy" del panel Emergent; los cambios solo van a producción al desplegar.',
      'El cliente no recibe el email → revisa Spam, comprueba en Bonos / Reservas que el email esté bien escrito, y usa "Reenviar".',
      'El calendario se queda cargando → recarga la página; los datos en MongoDB están seguros.',
      'Pago Redsys fallido → el cliente verá pantalla de error y NO se le habrá cobrado. Que vuelva a intentar o le creas la reserva manual.',
    ],
  },
];

export const HelpTab = () => {
  const [open, setOpen] = useState({ intro: true, workflows: true });

  const toggle = (id) => setOpen((p) => ({ ...p, [id]: !p[id] }));

  return (
    <div className="space-y-6" data-testid="help-tab">
      <div>
        <h2 className="text-2xl font-heading text-halo-charcoal flex items-center gap-2">
          <HelpCircle size={22} /> Ayuda y tutorial
        </h2>
        <p className="text-sm text-halo-text">
          Guía rápida para aprender a usar cada sección del panel. Pulsa en cualquier
          apartado para expandirlo.
        </p>
      </div>

      <div className="bg-halo-cream rounded-xl p-5 border border-halo-taupe/15">
        <h3 className="font-medium text-halo-charcoal mb-2 flex items-center gap-2">
          <Lightbulb size={16} /> Consejo
        </h3>
        <p className="text-sm text-halo-text leading-relaxed">
          La primera vez que entres, lee primero <strong>"¿Qué es este panel?"</strong> y
          luego <strong>"Flujos típicos"</strong>. Te dará un mapa mental completo en 2 minutos.
        </p>
      </div>

      <div className="space-y-2">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const expanded = !!open[s.id];
          return (
            <div
              key={s.id}
              className="bg-white border border-halo-taupe/15 rounded-xl overflow-hidden"
              data-testid={`help-section-${s.id}`}
            >
              <button
                type="button"
                onClick={() => toggle(s.id)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-halo-cream/40 transition-colors"
              >
                <span className="flex items-center gap-3 text-halo-charcoal font-medium">
                  <Icon size={18} className="text-halo-brown" />
                  {s.title}
                </span>
                <ChevronDown
                  size={18}
                  className={`text-halo-text transition-transform ${expanded ? 'rotate-180' : ''}`}
                />
              </button>
              {expanded && (
                <div className="px-5 pb-5 pt-1 border-t border-halo-taupe/10">
                  <ul className="space-y-2 text-sm text-halo-text leading-relaxed">
                    {s.bullets.map((b, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-halo-brown mt-0.5">•</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-halo-charcoal text-halo-cream rounded-xl p-5">
        <h3 className="font-medium mb-2 flex items-center gap-2">
          <HelpCircle size={16} /> ¿Sigues con dudas?
        </h3>
        <p className="text-sm text-halo-cream/80 mb-3">
          Si algo no encaja con la realidad, escríbenos y lo arreglamos. Este tutorial
          es estático: si añadimos funciones nuevas, también añadimos su sección aquí.
        </p>
        <div className="flex flex-wrap gap-3 text-sm">
          <a href="mailto:info@halocuevas.com" className="inline-flex items-center gap-1 underline">
            <Mail size={14} /> info@halocuevas.com
          </a>
          <a href="tel:678901086" className="inline-flex items-center gap-1 underline">
            <Phone size={14} /> 678 901 086
          </a>
        </div>
      </div>
    </div>
  );
};

export default HelpTab;
